import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { UserProfile, UserRole } from "../types";
import { PendingApprovalView } from "./PendingApprovalView";

interface ProtectedRouteProps {
  children: React.ReactNode;
  user: UserProfile | null;
  loading: boolean;
  allowedRoles?: UserRole[];
  onRefresh?: () => Promise<void> | void;
  onSignOut?: () => Promise<void> | void;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  user,
  loading,
  allowedRoles,
  onRefresh,
  onSignOut,
}) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-teal-500 border-t-transparent" />
          <p className="text-sm font-medium text-slate-600">
            Verifying access...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user.email.endsWith("@mlconnections.com")) {
    return (
      <div className="flex h-screen items-center justify-center p-6 text-center">
        <div className="glass max-w-md rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-red-600">Access Restricted</h2>
          <p className="mt-2 text-sm text-slate-600">
            Only accounts with the <strong>@mlconnections.com</strong> Google
            Workspace domain are authorized.
          </p>
        </div>
      </div>
    );
  }

  if (user.status === "pending_approval") {
    return (
      <PendingApprovalView
        user={user}
        onRefresh={onRefresh}
        onSignOut={onSignOut}
      />
    );
  }

  if (user.status === "suspended") {
    return (
      <div className="flex h-screen items-center justify-center p-6 text-center">
        <div className="glass max-w-md rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-red-600">Account Suspended</h2>
          <p className="mt-2 text-sm text-slate-600">
            Your access has been suspended. Please contact IT support.
          </p>
        </div>
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex h-screen items-center justify-center p-6 text-center">
        <div className="glass max-w-md rounded-2xl p-8 shadow-xl">
          <h2 className="text-xl font-bold text-slate-800">
            Insufficient Privileges
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            You do not have permission to access this administrative area.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
